import { useEffect, useMemo, useState } from 'react';

import { utils } from '@atlaskit/util-service-support/constants';
import type { RequestServiceOptions } from '@atlaskit/util-service-support/types';

type Maybe<T> = T | null;

type RESTRequest<Variables> = {
	body: Variables;
	url: string;
};

type RESTResponse<Data> = Maybe<Data>;

export type ObjectResolverParamType = {
	resourceUrl: string;
};

export type ObjectResolverDataType = {
	body: Body;
	error?: {
		message: string;
		status: number;
		type: string;
	};
	status: number;
};

export type Body = {
	data: ObjectData;
	meta: Meta;
};

export type ObjectData = {
	'@context': Context;
	'@type': string[];
	'atlassian:ari': string;
	'atlassian:ownedBy': AtlassianOwnedBy;
	'atlassian:reactCount': number;
	'atlassian:state': string;
	'atlassian:titlePrefix': AtlassianTitlePrefix;
	attributedTo: AtlassianOwnedBy;
	generator: Generator;
	name: string;
	preview: Preview;
	'schema:commentCount': number;
	'schema:dateCreated': Date;
	summary: string;
	updated: Date;
	url: string;
};

export type Context = {
	'@vocab': string;
	atlassian: string;
	schema: string;
};

export type AtlassianOwnedBy = {
	'@type': string;
	icon: string;
	name: string;
};

export type AtlassianTitlePrefix = {
	'@type': string;
	text: string;
};

export type Generator = {
	'@id': string;
	'@type': string;
	icon: Icon;
	name: string;
};

export type Icon = {
	'@type': string;
	url: string;
};

export type Preview = {
	'@type': string;
	'atlassian:supportedPlatforms': string[];
	href: string;
};

export type Meta = {
	access: string;
	activationId: string;
	auth: any[];
	category: string;
	containerId: string;
	definitionId: string;
	key: string;
	objectId: string;
	product: string;
	resourceType: string;
	supportedFeature: string[];
	tenantId: string;
	visibility: string;
};

interface useFetchSmartLinkDataArgs {
	urls: string[];
}

async function fetchRestResponse<Variables, Data>({ url, body }: RESTRequest<Variables>) {
	const options: RequestServiceOptions = {
		requestInit: {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(body),
		},
	};
	return utils.requestService<RESTResponse<Data>>({ url }, options);
}

const OBJECT_RESOLVER_ENDPOINT = '/gateway/api/object-resolver/resolve/batch';

export const useFetchSmartLinkData = ({
	urls,
}: useFetchSmartLinkDataArgs): {
	isLoading: boolean;
	objectResolverData: ObjectData[];
} => {
	const [isLoading, setIsLoading] = useState(true);
	const [objectResolverData, setObjectResolverData] = useState<ObjectData[]>([]);
	const urlKeys = useMemo(() => urls.join('|'), [urls]);

	useEffect(() => {
		const fetchObjectResolver = async () => {
			if (!urls.length) {
				setIsLoading(false);
				return;
			}

			try {
				setIsLoading(true);
				const objectResolver = await fetchRestResponse<
					ObjectResolverParamType[],
					ObjectResolverDataType[]
				>({
					url: OBJECT_RESOLVER_ENDPOINT,
					body: urls.map((url) => ({ resourceUrl: url })),
				});

				if (!objectResolver) {
					setIsLoading(false);
					return;
				}

				const objectData = objectResolver.filter((obj) => obj.status === 200);
				setObjectResolverData(objectData.map((objData) => objData.body.data));
				setIsLoading(false);
			} catch {
				setIsLoading(false);
			}
		};

		void fetchObjectResolver();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [urlKeys]);

	return {
		isLoading,
		objectResolverData,
	};
};
